import React, {
  useState, useEffect, useMemo, useRef,
} from "react";

import { toast } from "react-toastify";

import useAbortController from "../../../Common/hooks/useAbortController";
import { useAccessStatus } from "../../../grace-period/hooks/useAccessStatus";
import ConfirmationModal from "../../../Onboarding/UpdateOnboardingModal/ConfirmationModal";
import ActionCell from "../../Common/ActionCell";
import RequestTable from "../../Common/RequestTable";
import SearchAndCreate from "../../Common/SearchAndCreate";
import SupplyPickupLanding from "../../SupplyPickupLanding";
import PickupDetailsModal from "./Modals/PickupDetailsModal";
import SelectPickupTypeModal from "./Modals/SelectPickupTypeModal";
import ShippingLabelModal from "./Modals/ShippingLabelModal";
// import PickupStatsCards from "./PickupStatsCards";

import { downloadFileFromS3WithPresigned } from "../../../../utils/helpers";
import { PICKUP_CATEGORIES } from "./utils";

import { getCustomerAddress } from "../../../../actions/sampleSubmission";
import { fetchPickupRequests, editPickupRequest } from "../../../../actions/supplyPickup";

import "../SupplyPickupTabGroup.css";

const LABELS_CATEGORIES = new Set([
  PICKUP_CATEGORIES.FEDEX_PICKUP_LABELS,
  PICKUP_CATEGORIES.FEDEX_DROPOFF_LABELS,
]);

const getLabelS3Path = (label) => label?.label_s3_path || null;

export default function PickupTab({
  onCreateEditClick,
  initialPickupResponse = null,
  showLanding = false,
}) {
  const { areResultsLocked } = useAccessStatus();
  const allColumns = [
    { name: "Order ID", key: "pickup_order_id" },
    { name: "Order Date", key: "order_date" },
    { name: "Requested by", key: "requested_by" },
    { name: "Pickup Date", key: "pickup_date" },
    { name: "Delivery Partner", key: "pickup_category" },
    { name: "Status", key: "status" },
    { name: "Total Labels", key: "total_labels" },
    { name: "Items", key: "items" },
    { name: "Total Submissions", key: "total_submissions" },
    {
      name: " ",
      key: "action",
      className: "SupplyTableAction short-width",
      cell: <ActionCell />,
    },
  ];
  const [columns] = useState(allColumns);

  const [openSelectType, setOpenSelectType] = useState(false);
  const [createPickupLoading, setCreatePickupLoading] = useState(false);
  const [customerLogistics, setCustomerLogistics] = useState(null);

  const statuses = [
    { value: "exclude-cancelled", label: "All Pickups" },
    { value: "requested", label: "Pickup Scheduled" },
    { value: "picked_up", label: "Picked Up" },
    { value: "in_transit", label: "In Transit" },
    { value: "delivered", label: "Received At lab" },
    { value: "cancelled", label: "Cancelled" },
  ];
  const defaultStatus = "exclude-cancelled";
  const [currentStatus, setCurrentStatus] = useState(defaultStatus);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [openPickupDetailsModal, setOpenPickupDetailsModal] = useState(false);
  const [showConfimationModal, setShowConfimationModal] = useState(false);
  const [labelModalOpen, setLabelModalOpen] = useState(false);
  const [labelModalLabels, setLabelModalLabels] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingNewContent, setLoadingNewContent] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reportDates, setReportDates] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState({ nextPageLink: "", currentPage: 1, totalPages: 1 });
  const requestTableScrollbarRef = useRef();
  const [search, setSearch] = useState("");
  const [dates, setDates] = useState({ date_from: "", date_to: "" });
  const [filters, setFilters] = useState({
    orderDate: "",
    pickupDate: "",
    orderId: "",
    requestedBy: "",
  });
  const [actionMap, setActionMap] = useState({
    menu: {
      cancel: true,
    },
  });
  const pickupCategories = [
    { value: PICKUP_CATEGORIES.LOCAL_COURIER, label: "Local Courier" },
    { value: PICKUP_CATEGORIES.FEDEX_PICKUP_LABELS, label: "FedEx Pickup" },
    { value: PICKUP_CATEGORIES.FEDEX_PICKUP_NO_LABELS, label: "FedEx Pickup - Billable Stamp" },
    { value: PICKUP_CATEGORIES.FEDEX_DROPOFF_LABELS, label: "FedEx Drop-off" },
    { value: PICKUP_CATEGORIES.FEDEX_DROPOFF_NO_LABELS, label: "FedEx Drop-off - Billable Stamp" },
  ];
  const [selectedPickupCategory, setSelectedPickupCategory] = useState("");
  const [requestedByLst, setRequestedByLst] = useState([]);
  const { setNewAbortController, clearAbortController } = useAbortController();

  const requestedByOptions = useMemo(() => {
    const fromApi = (requestedByLst || [])
      .map((item) => {
        const value = typeof item === "string"
          ? item
          : (item?.requested_by || item?.value || item?.user_name || "");
        if (!value) return null;
        return { label: value, value };
      })
      .filter(Boolean);

    if (fromApi.length > 0) return fromApi;

    const unique = [...new Set(
      (requests || [])
        .map((row) => row?.requested_by)
        .filter(Boolean),
    )].sort((a, b) => a.localeCompare(b));

    return unique.map((value) => ({ label: value, value }));
  }, [requestedByLst, requests]);

  const applyPickupResponse = (response) => {
    const {
      success, message, result, canceled, dates: rpDates, next_page_link, total_page,
      requested_by_lst,
    } = response;
    if (success) {
      setPaginationInfo({ nextPageLink: next_page_link, currentPage: 1, totalPages: total_page });
      setRequests(result || []);
      setReportDates(rpDates);
      if (requested_by_lst) {
        setRequestedByLst(requested_by_lst);
      }
    } else if (!canceled) {
      toast.error(message);
      setPaginationInfo({ nextPageLink: "", currentPage: 1, totalPages: 1 });
      setRequests([]);
      setReportDates([]);
    }
    setLoading(false);
  };

  const fetchRequests = async (params = {}, callback = () => { }) => {
    const page = params.page !== undefined ? Number(params.page) : 1;

    if (page > 1) {
      setLoadingNewContent(true);
    } else {
      setLoading(true);
      if (requestTableScrollbarRef.current) {
        requestTableScrollbarRef.current.scrollToTop();
      }
    }

    const newAbortController = setNewAbortController();
    const payload = {
      pickup_order_id: params.search !== undefined ? params.search : search,
      status: params.status !== undefined ? params.status : (currentStatus || defaultStatus),
      pickup_category: params.pickup_category !== undefined
        ? params.pickup_category
        : selectedPickupCategory,
      date_from: params.date_from !== undefined ? params.date_from : dates.date_from,
      date_to: params.date_to !== undefined ? params.date_to : dates.date_to,
      pickup_date: params.pickup_date !== undefined ? params.pickup_date : filters.pickupDate,
      requested_by: params.requested_by !== undefined ? params.requested_by : filters.requestedBy,
      page,
    };
    const {
      success, message, result, canceled, dates: rpDates, next_page_link, total_page,
      requested_by_lst,
    } = await fetchPickupRequests(payload, newAbortController.signal);

    if (newAbortController.signal.aborted) {
      if (page > 1) {
        setLoadingNewContent(false);
      }
      return;
    }

    if (success) {
      setPaginationInfo({ nextPageLink: next_page_link, currentPage: page, totalPages: total_page });
      setRequests((prev) => (page > 1 ? [...prev, ...result] : result));
      setReportDates(rpDates);
      if (requested_by_lst) {
        setRequestedByLst(requested_by_lst);
      }
    } else if (!canceled) {
      toast.error(message);
      if (page > 1) {
        if (requestTableScrollbarRef.current) {
          requestTableScrollbarRef.current.scrollTop -= 100;
        }
      } else {
        setPaginationInfo({ nextPageLink: "", currentPage: 1, totalPages: 1 });
        setRequests([]);
        setReportDates([]);
      }
    }

    clearAbortController();

    if (page > 1) {
      if (requestTableScrollbarRef.current) {
        requestTableScrollbarRef.current.scrollTop -= 4;
      }
      setLoadingNewContent(false);
    } else {
      setLoading(false);
      if (requestTableScrollbarRef.current) {
        requestTableScrollbarRef.current.scrollToTop();
      }
    }

    if (success || !canceled) {
      callback();
    }
  };

  useEffect(() => {
    if (initialPickupResponse) {
      applyPickupResponse(initialPickupResponse);
    }
  }, [initialPickupResponse]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loadingNewContent && requestTableScrollbarRef.current) {
      requestTableScrollbarRef.current.scrollToBottom();
    }
  }, [loadingNewContent]);

  const handleCancelRequest = async () => {
    setDeleting(true);
    const payload = {
      internal: {
        pickup_order_id: selectedRowData.pickup_order_id,
        status: "cancelled",
      },
    };
    const { success, message } = await editPickupRequest(payload);
    if (success) {
      setShowConfimationModal(false);
      toast.success(message);
      fetchRequests({ page: 1 });
    } else {
      toast.error(message);
    }
    setDeleting(false);
  };

  const handleSearch = (params) => {
    if (params.search !== undefined) {
      setFilters((prev) => ({ ...prev, orderId: params.search || "" }));
    }
    fetchRequests({ ...params, page: 1 });
  };

  const handleApplyFilters = (nextFilters) => {
    const orderDate = nextFilters.orderDate || "";
    const pickupDate = nextFilters.pickupDate || "";
    const orderId = nextFilters.orderId || "";
    const requestedBy = nextFilters.requestedBy || "";

    setFilters({
      orderDate,
      pickupDate,
      orderId,
      requestedBy,
    });
    setSearch(orderId);
    setDates({
      date_from: orderDate,
      date_to: orderDate,
    });
    fetchRequests({
      search: orderId,
      date_from: orderDate,
      date_to: orderDate,
      pickup_date: pickupDate,
      requested_by: requestedBy,
      page: 1,
    });
  };

  const updateActionMap = (status) => {
    setActionMap({
      menu: {
        cancel: status === "requested" || status === "exclude-cancelled",
      },
    });
  };

  const handleDeliveryPartnerChange = (value) => {
    const category = value || "";
    setSelectedPickupCategory(category);
    fetchRequests({ pickup_category: category, page: 1 });
  };

  const handleClearAllFilters = () => {
    setSelectedPickupCategory("");
    setFilters({
      orderDate: "",
      pickupDate: "",
      orderId: "",
      requestedBy: "",
    });
    setSearch("");
    setDates({ date_from: "", date_to: "" });
    fetchRequests({
      search: "",
      date_from: "",
      date_to: "",
      pickup_date: "",
      requested_by: "",
      pickup_category: "",
      page: 1,
    });
  };

  const handleStatusOnChange = (value) => {
    const status = value || defaultStatus;
    setCurrentStatus(status);
    updateActionMap(status);
    fetchRequests({ status, page: 1 });
  };

  /**
   * Handle different user actions of the table row
   * @param {"row-click" | "delete-row"} type
   */
  const handleRowActions = (type) => ({ data }) => {
    setSelectedRowData(data);
    if (type === "row-click") {
      setOpenPickupDetailsModal(true);
    }
    if (type === "delete-row") {
      setShowConfimationModal(true);
    }
  };

  const handleViewOrderSummary = ({ data }) => {
    setSelectedRowData(data);
    setOpenPickupDetailsModal(true);
  };

  const handlePrintLabel = ({ data }) => {
    const labels = Array.isArray(data?.fedex_labels) ? data.fedex_labels : [];
    const hasPrintable = labels.some(
      (label) => getLabelS3Path(label) || label?.label_base64,
    );
    if (!hasPrintable) {
      toast.error("No shipping label is available for this pickup.");
      return;
    }
    setSelectedRowData(data);
    setLabelModalLabels(labels);
    setLabelModalOpen(true);
  };

  const handleDownloadLabel = async ({ data }) => {
    const labels = Array.isArray(data?.fedex_labels) ? data.fedex_labels : [];
    const labelsWithPath = labels.filter((label) => getLabelS3Path(label));
    if (!LABELS_CATEGORIES.has(data?.pickup_category) || labelsWithPath.length === 0) {
      toast.error("No shipping label is available for this pickup.");
      return;
    }

    const paths = labelsWithPath.map((label) => getLabelS3Path(label));
    const fileNames = labelsWithPath.map(
      (label, index) => `shipping-label-${label.tracking_number || index + 1}.pdf`,
    );
    const success = await downloadFileFromS3WithPresigned(paths, "private", fileNames);
    if (!success) {
      toast.error("Unable to download shipping label.");
    }
  };

  const handleCreatePickupClick = async () => {
    if (areResultsLocked || createPickupLoading) return;
    setCreatePickupLoading(true);

    const { success, message, address } = await getCustomerAddress(null, "");

    if (success) {
      setCustomerLogistics(address?.logistics ?? null);
    } else {
      toast.error(message || "Failed to load pickup options");
      // Fall back to FedEx Drop-Off only when customer info cannot be loaded
      setCustomerLogistics(null);
    }

    setCreatePickupLoading(false);
    setOpenSelectType(true);
  };

  if (showLanding) {
    return (
      <>
        <SupplyPickupLanding
          tab="pickup"
          createPickupLoading={createPickupLoading}
          onCreatePickupClick={handleCreatePickupClick}
        />
        <SelectPickupTypeModal
          open={openSelectType}
          logistics={customerLogistics}
          onCancel={() => setOpenSelectType(false)}
          onSelect={(data) => {
            setOpenSelectType(false);
            onCreateEditClick(data);
          }}
        />
      </>
    );
  }

  return (
    <>
      <div className="SupplyPickupMainDiv SupplyPickupMainDiv--pickup">
        {/* <PickupStatsCards requests={requests} /> */}
        <div className="SupplyPickupStickyToolbar">
          <SearchAndCreate
            type="pickup"
            createBtnLabel="Create Pickup Request"
            btnClassName="SupplyPickupCreateBtn"
            btnOnClick={handleCreatePickupClick}
            btnLoading={createPickupLoading}
            createDisabled={areResultsLocked}
            handleSearch={handleSearch}
            search={search}
            setSearch={setSearch}
            dates={dates}
            setDates={setDates}
            getReportDates={() => reportDates}
            pickupCategories={pickupCategories}
            selectedPickupCategory={selectedPickupCategory}
            handleDeliveryPartnerChange={handleDeliveryPartnerChange}
            filters={filters}
            onApplyFilters={handleApplyFilters}
            onClearAllFilters={handleClearAllFilters}
            requestedByOptions={requestedByOptions}
          />
          <div className="SupplyPickupStatusTabs">
            {statuses.map((status) => (
              <button
                key={status.value}
                type="button"
                className={`SupplyPickupStatusTabs__Tab${currentStatus === status.value ? " SupplyPickupStatusTabs__Tab--active" : ""}`}
                onClick={() => handleStatusOnChange(status.value)}
              >
                {status.label}
              </button>
            ))}
          </div>
        </div>
        <RequestTable
          loading={loading}
          loadingNewContent={loadingNewContent}
          tableData={requests}
          columns={columns}
          onRowClick={handleRowActions("row-click")}
          onDeleteClick={handleRowActions("delete-row")}
          onPrintLabelClick={handlePrintLabel}
          onDownloadLabelClick={handleDownloadLabel}
          onViewOrderSummaryClick={handleViewOrderSummary}
          actionMap={actionMap}
          paginationInfo={paginationInfo}
          fetchRequests={fetchRequests}
          scrollbarRef={requestTableScrollbarRef}
          search={search}
        />
        <SelectPickupTypeModal
          open={openSelectType}
          logistics={customerLogistics}
          onCancel={() => setOpenSelectType(false)}
          onSelect={(data) => {
            setOpenSelectType(false);
            onCreateEditClick(data);
          }}
        />
      </div>
      {openPickupDetailsModal && selectedRowData?.pickup_order_id && (
        <PickupDetailsModal
          onCancel={() => {
            setSelectedRowData(null);
            setOpenPickupDetailsModal(false);
          }}
          pickupOrderId={selectedRowData.pickup_order_id}
        />
      )}
      {labelModalOpen && (
        <ShippingLabelModal
          open={labelModalOpen}
          labels={labelModalLabels}
          onCancel={() => {
            setLabelModalOpen(false);
            setLabelModalLabels([]);
          }}
        />
      )}
      {showConfimationModal && (
        <ConfirmationModal
          headerText="Cancel pickup request"
          bodyText="Are you sure you want to cancel pickup request?"
          buttonText={["Close", "Confirm"]}
          buttonFunctions={[
            () => setShowConfimationModal(false),
            handleCancelRequest,
          ]}
          loading={deleting}
        />
      )}
    </>
  );
}
